import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { v2, v2Unscoped, v2api, v2ErrorStatus } from './v2'
import {
  toCustomerOrderEntry,
  type CustomerOrderEntry,
  type CustomerReceiptRow,
} from '@/lib/customerOrders'
import { useSessionStore } from '@/stores/sessionStore'
import type { Customer } from '@/types'

// Query keys
export const customerKeys = {
  all: ['customers'] as const,
  search: (query: string) => [...customerKeys.all, 'search', query] as const,
  detail: (id: number) => [...customerKeys.all, 'detail', id] as const,
  recent: () => [...customerKeys.all, 'recent'] as const,
}

// Retail Customer is a system pseudo-record used as the default customer
// for walk-in / anonymous transactions. The backend persists it as a real
// row in `wholesale-clients` (so the API returns 200, not 404), but it has
// no real profile, insights, activity, or limits — opening the customer
// modal for it would just render empty stub state and confuse the cashier.
//
// Use `isRetailCustomer(id)` to gate UI affordances (clickable name,
// "Open profile" actions) so they only fire for real customers.
export const RETAIL_CUSTOMER_ID = 3332

export function isRetailCustomer(customerId: number | string | null | undefined): boolean {
  if (customerId == null) return false
  return Number(customerId) === RETAIL_CUSTOMER_ID
}

// Types for API responses
interface CustomerSearchResponse {
  customers: Customer[]
  total: number
}

interface CustomerCreateRequest {
  first_name: string
  last_name: string
  email?: string
  phone?: string
  // Two casings on purpose — different BE endpoints use different
  // names: the Customer model column is `birth_date`, the
  // /verify-age endpoint accepts `birthdate`. We pass both on create
  // so whichever the BE create form picks up is set, and add a
  // BE follow-up to align them. YYYY-MM-DD format (what the
  // <input type="date"> emits).
  birth_date?: string
  birthdate?: string
  // Daily cannabis limits in grams, formatted to 2 decimals as a
  // string (the wire shape the BE Decimal field expects). The hook
  // defaults both to the active store's `cannabis_limit` (or 30g
  // when missing) so a fresh customer inherits the provincial cap.
  flowers_limit?: string
  flowers_flower_limit?: string
  is_medical?: boolean
  medical_card_number?: string
  medical_card_expiry?: string
  // BE Django CustomerForm requires `group_id` (ModelChoiceField with no
  // `required=False`). Without it the POST 400s with
  // `{group_id: ["This field is required."]}`. Callers should pass the
  // selected customer-group id, or fall back to the first id from
  // /select-customer-groups when the user didn't pick one.
  group_id?: number
  // BE form also requires `points` — there's no FE control for it on
  // create, so `useCreateCustomer` defaults to 0. Optional in the type
  // so callers can omit it; the hook fills it in.
  points?: number
  // Anchor a new customer to the current register's store. Set by the
  // hook from the session's selectedStore so customers created at this
  // till are tied to it for reporting / multi-store ownership.
  home_store?: number
}

interface CustomerUpdateRequest extends Partial<CustomerCreateRequest> {
  notes?: string
}

// Editable fields on the rich wholesale-clients record.
export interface CustomerOverviewUpdateRequest {
  first_name?: string
  last_name?: string
  email?: string | null
  phone?: string | null
  birth_date?: string | null
  group_id?: number | null
  home_store?: number | null
  notes?: string | null
  comments?: string
}

// Search customers by phone, email, or name
export function useCustomerSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: customerKeys.search(query),
    queryFn: async (): Promise<Customer[]> => {
      const data = await v2api.get<CustomerSearchResponse | Customer[]>(v2('/customers'), {
        q: query,
      })
      // Handle both array and object response formats
      if (Array.isArray(data)) return data
      return data?.customers ?? []
    },
    enabled: enabled && query.length >= 2,
    staleTime: 1000 * 30, // customer context can change during active POS operations
  })
}

// Get single customer details
export function useCustomer(customerId: number | null) {
  return useQuery({
    queryKey: customerKeys.detail(customerId!),
    queryFn: async (): Promise<Customer> => {
      return v2api.get<Customer>(v2Unscoped(`/objects/customers/${customerId}`))
    },
    enabled: !!customerId,
    staleTime: 1000 * 30,
  })
}

// Get recently used customers
export function useRecentCustomers(limit = 10) {
  return useQuery({
    queryKey: customerKeys.recent(),
    queryFn: async (): Promise<Customer[]> => {
      const data = await v2api.get<CustomerSearchResponse | Customer[]>(v2('/customers'), {
        sort_by: 'last_visit',
        sort_order: 'desc',
        per_page: limit,
      })
      // Handle both array and object response formats
      if (Array.isArray(data)) return data
      return data?.customers ?? []
    },
    staleTime: 1000 * 60 * 2,
  })
}

// Create new customer
export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CustomerCreateRequest): Promise<Customer> => {
      // The legacy Django form requires `points` (no `required=False`
      // on the ModelForm). New customers always start at 0; the cashier
      // never sets initial points from the create modal, so default
      // here rather than at every call site. Caller-supplied values
      // still win.
      //
      // `home_store` is sourced from the active session — every
      // customer created at a till should be tied to that till's
      // store. The cashier doesn't see a store picker on the create
      // form (multi-store assignment happens elsewhere), so the
      // session is the only sensible default.
      //
      // `flowers_limit` / `flowers_flower_limit` default to the
      // store's `cannabis_limit` so a newly-created customer inherits
      // the provincial cap (Canadian default 30g). Mirrors the
      // legacy wholesale-clients form behavior at
      // project/dictionaries/views/customers.py:267 which stamps
      // `store.cannabis_limit or 30` on create. Passed as strings
      // formatted to 2 decimals — matches the wire shape the BE
      // Decimal field expects (e.g. "30.00").
      const session = useSessionStore.getState()
      const sessionStoreId = session.selectedStore?.id
      const cannabisLimit = session.selectedStore?.cannabis_limit ?? 30
      const cannabisLimitStr = Number(cannabisLimit).toFixed(2)
      return v2api.post<Customer>(v2Unscoped('/objects/customers'), {
        points: 0,
        flowers_limit: cannabisLimitStr,
        flowers_flower_limit: cannabisLimitStr,
        ...(sessionStoreId != null ? { home_store: sessionStoreId } : {}),
        ...data,
      })
    },
    onSuccess: (newCustomer) => {
      // Add to cache
      queryClient.setQueryData(customerKeys.detail(newCustomer.id), newCustomer)
      // Invalidate search results
      queryClient.invalidateQueries({ queryKey: customerKeys.all })
    },
  })
}

// Update existing customer
export function useUpdateCustomer() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, ...data }: CustomerUpdateRequest & { id: number }): Promise<Customer> => {
      return v2api.patch<Customer>(v2Unscoped(`/objects/customers/${id}`), data)
    },
    onSuccess: (updatedCustomer) => {
      // Update cache
      queryClient.setQueryData(customerKeys.detail(updatedCustomer.id), updatedCustomer)
      // Invalidate search results
      queryClient.invalidateQueries({ queryKey: customerKeys.all })
    },
  })
}

// Verify customer age (cannabis compliance)
// v2: customer_id moves from body to URL path; body is just {birthdate}.
// (Previously a 404 in v1 — this endpoint actually works in v2.)
export function useVerifyAge() {
  return useMutation({
    mutationFn: async (params: { customer_id: number; birthdate: string }): Promise<{ verified: boolean; age: number }> => {
      return v2api.post(
        v2Unscoped(`/customers/${params.customer_id}/verify-age`),
        { birthdate: params.birthdate },
      )
    },
  })
}

// ---------------------------------------------------------------------------
// Customer Insights — frequently-purchased items + summary stats
// GET /ht/api/customers/insights?customer_id=X&limit=N
// ---------------------------------------------------------------------------

export interface CustomerInsightItem {
  sku: string
  unit_name: string
  product_id: number
  on_hand: number
  last_purchased: string // "MM/DD/YYYY"
  total_qty: number
  price: number
  product_name: string
  uom: string
  brand_name: string
}

export interface CustomerInsightBrand {
  id: number
  name: string
  qty: number
}

export interface CustomerInsightCategory {
  name: string
  qty: number
}

export interface CustomerInsightSummary {
  order_count: number
  top_brands: CustomerInsightBrand[]
  lifetime_spend: number
  top_categories: CustomerInsightCategory[]
  last_order: string | null // "MM/DD/YYYY"
  avg_items_per_order: number
}

export interface CustomerInsights {
  items: CustomerInsightItem[]
  summary: CustomerInsightSummary
}

export function useCustomerInsights(customerId: number | null, limit = 100) {
  return useQuery({
    queryKey: [...customerKeys.detail(customerId!), 'insights', limit],
    queryFn: async (): Promise<CustomerInsights> => {
      return v2api.get<CustomerInsights>(v2Unscoped('/customers/insights'), {
        customer_id: customerId,
        limit,
      })
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 5,
  })
}

// ---------------------------------------------------------------------------
// Customer Activity -- the customer's paid receipts from EVERY store
//
// GET /ht/api/v2/customers/{id}/receipts?limit=N&offset=M  (unscoped)
//
// Paged: the route answers `has_more` alongside the rows, so the panel
// appends a page at a time ("Load more") instead of pinning the history
// at the first twenty receipts.
//
// The register-scoped `/receipts/search` is pinned to the URL store by the
// portal (`_scope()` 403s any other store), so it can only ever answer
// "what did this customer buy HERE" -- while the Insights tab beside it
// already aggregates chain-wide. The dedicated route filters server-side
// (Paid, not held -- the old `any: true` fetch filtered AFTER the LIMIT,
// so a customer whose newest twenty receipts were deleted carts read "No
// past orders") and carries `store_id` / `store_name` per row so the
// panel can label receipts rung elsewhere.
//
// Old portal without the route -> 404 -> fall back to this store's
// receipts/search, i.e. exactly the pre-existing behaviour.
// ---------------------------------------------------------------------------

export type CustomerActivityEntry = CustomerOrderEntry

export interface CustomerActivityPage {
  entries: CustomerActivityEntry[]
  /** Offset to ask for next, or null when this was the last page. */
  nextOffset: number | null
}

export function useCustomerActivity(customerId: number | null, limit = 20) {
  const sessionStoreId = useSessionStore((s) => s.selectedStore?.id ?? null)
  return useInfiniteQuery({
    queryKey: [
      ...customerKeys.detail(customerId!),
      'activity', 'completed', 'all-stores', sessionStoreId, limit,
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<CustomerActivityPage> => {
      const offset = pageParam
      try {
        const data = await v2api.get<{
          receipts?: CustomerReceiptRow[]
          has_more?: boolean
          limit?: number
          offset?: number
        }>(
          v2Unscoped(`/customers/${customerId}/receipts`),
          { limit, offset },
        )
        const rows = data?.receipts ?? []
        // Step by what the SERVER paged with, not what we asked for: it
        // normalises both values (clamp_page) and echoes the result, so
        // a clamped limit or offset can't leave us re-reading or
        // skipping a window.
        const pageLimit = typeof data?.limit === 'number' && data.limit > 0 ? data.limit : limit
        const pageOffset =
          typeof data?.offset === 'number' && data.offset >= 0 ? data.offset : offset
        return {
          entries: rows
            // Server already filters; kept as a guard so a row shape drift
            // can never surface an unpaid cart.
            .filter(isCompletedReceiptForActivity)
            .map((r) => toCustomerOrderEntry(r, sessionStoreId)),
          // Two conditions, both required. `has_more` is the server's
          // answer and already accounts for its max-offset clamp (past
          // which the last page repeats forever). The short-page check
          // is the belt-and-braces half for an older build that omits
          // the field -- and it counts RAW rows, not filtered entries:
          // a page whose receipts were all filtered out here is still a
          // full page server-side.
          nextOffset:
            data?.has_more !== false && rows.length >= pageLimit
              ? pageOffset + pageLimit
              : null,
        }
      } catch (err) {
        // A route the portal doesn't have yet answers with tornado's bare
        // HTML 404, which v2api rethrows as the raw transport error (only
        // envelope bodies become V2ApiError) -- so read the status off
        // either shape. Anything but 404 is a real failure.
        if (v2ErrorStatus(err) !== 404) throw err
      }

      // Legacy path (portal predates the route): this store only, and a
      // single page -- /receipts/search takes no offset here, so the
      // fallback never advertises a next page.
      if (offset > 0) return { entries: [], nextOffset: null }
      const data = await v2api.get<unknown>(v2('/receipts/search'), {
        customer_id: customerId,
        any: true,
        limit,
      })
      const list = Array.isArray(data)
        ? data
        : (data as { receipts?: unknown[] })?.receipts ?? []
      return {
        entries: list
          .filter(isCompletedReceiptForActivity)
          .map((r) =>
            toCustomerOrderEntry(
              { ...(r as CustomerReceiptRow), store_id: sessionStoreId },
              sessionStoreId,
            ),
          ),
        nextOffset: null,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!customerId,
    staleTime: 1000 * 60,
  })
}

// Same predicate as receipts.ts `isCompletedOrder` but local-scoped so
// we don't expand customers.ts → receipts.ts coupling. Keep in sync
// with that one if statuses change.
function isCompletedReceiptForActivity(r: unknown): boolean {
  if (!r || typeof r !== 'object') return false
  const x = r as {
    status?: number | string | null
    on_hold?: boolean
    total?: number | string | null
    totals?: { total?: number | string | null } | null
    is_return?: boolean
    src_id?: number | null
    src_number?: string | number | null
    source_number?: string | number | null
    return_number?: string | number | null
    return_total?: number | string | null
    return_receipt_id?: number | null
  }
  if (x.on_hold === true) return false
  const status = Number(x.status)
  // NEW(0), PAYING(2), HOLD(4), DELETED(-1), CANCELED(8) — exclude all.
  if (status === 0) return false
  if (status === 2) return false
  if (status === 4) return false
  if (status === -1) return false
  if (status === 8) return false
  // Orphan negative totals (refunds with no return-link) → skip.
  const total = parseFloat(String(x.totals?.total ?? x.total ?? 0))
  const hasReturnLink = Boolean(
    x.is_return === true
    || x.src_id
    || x.src_number
    || x.source_number
    || x.return_number
    || x.return_total
    || x.return_receipt_id,
  )
  if (total < 0 && !hasReturnLink) return false
  return true
}

// ---------------------------------------------------------------------------
// Customer Overview — full record (the rich "wholesale-clients" object)
// GET /ht/api/objects/wholesale-clients/:id
// Used to power Profile/Loyalty/Daily-limit details that the lighter
// `/objects/customers` payload doesn't include.
// ---------------------------------------------------------------------------

export interface CustomerAilment {
  ailment: number
  name: string
  value: boolean
}

export interface CustomerGroup {
  id: number
  name: string
  type?: number
  reward_type?: number
  reward_name?: string | null
}

export interface CustomerOverview {
  id: number
  first_name?: string
  last_name?: string
  full_name?: string
  display_name?: string
  email?: string
  phone?: string
  birth_date?: string | null
  medical_card_id?: string
  medical_card_date_issued?: string | null
  medical_card_date_exp?: string | null
  ailments?: CustomerAilment[]
  notes?: string
  comments?: string
  additional_notes?: string
  group?: CustomerGroup | null
  points?: string | number
  account_balance?: string | number
  account_balance_store?: string | number
  current_balance?: string | number
  flowers_limit?: string | number
  flowers_flower_limit?: string | number
  start_date?: string | null
  start_date__hr?: string
  last_purchase_date?: string | null
  is_blocked?: boolean
  inactive?: boolean
  is_on_hold?: boolean
  springbig_balance?: string | number
  home_store?: number | null
  group_id?: number | null
}

export function useCustomerOverview(customerId: number | null) {
  return useQuery({
    queryKey: [...customerKeys.detail(customerId!), 'overview'],
    queryFn: async (): Promise<CustomerOverview> => {
      return v2api.get<CustomerOverview>(v2Unscoped(`/objects/wholesale-clients/${customerId}`))
    },
    enabled: !!customerId,
    staleTime: 1000 * 60,
  })
}

// Update editable fields on the rich overview record.
//
// The wholesale-clients endpoint expects a *full* record via PUT (not a
// PATCH) — the backend replaces the row wholesale, so omitting fields wipes
// them out. To avoid forcing every caller to know about all 100+ legacy
// fields (billing addresses, tax flags, qa_*, etc.), this hook reads the
// latest cached/fetched overview, merges the requested changes on top, and
// sends the full payload back.
export function useUpdateCustomerOverview() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...changes
    }: CustomerOverviewUpdateRequest & { id: number }): Promise<CustomerOverview> => {
      const overviewKey = [...customerKeys.detail(id), 'overview'] as const

      // Pull the freshest copy of the full record. fetchQuery hits the cache
      // when fresh, otherwise refetches. We type it loosely (Record) since
      // the API has many fields we do not model in CustomerOverview.
      const current = await queryClient.fetchQuery({
        queryKey: overviewKey,
        queryFn: async (): Promise<CustomerOverview> =>
          v2api.get<CustomerOverview>(v2Unscoped(`/objects/wholesale-clients/${id}`)),
      })

      // Strip undefined keys so we do not overwrite existing values with
      // explicit `undefined` (sending `null` is intentional and preserved).
      const cleanedChanges: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(changes)) {
        if (v !== undefined) cleanedChanges[k] = v
      }

      const payload: Record<string, unknown> = {
        ...(current as unknown as Record<string, unknown>),
        ...cleanedChanges,
      }

      // Backend mirrors group <-> group_id; keep them in sync so the new
      // selection sticks regardless of which key the server reads.
      if (cleanedChanges.group_id !== undefined) {
        payload.group = cleanedChanges.group_id
      }

      return v2api.put<CustomerOverview>(v2Unscoped(`/objects/wholesale-clients/${id}`), payload)
    },
    onSuccess: (updated, variables) => {
      queryClient.setQueryData([...customerKeys.detail(variables.id), 'overview'], updated)
      queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: customerKeys.all })
    },
  })
}

// ---------------------------------------------------------------------------
// Customer Groups (loyalty / rewards programs) — used to populate the
// "Rewards program" picker on the customer profile.
// GET /ht/api/select-customer-groups
// ---------------------------------------------------------------------------

interface CustomerGroupsListResponse {
  customer_groups?: CustomerGroup[]
  groups?: CustomerGroup[]
}

export const customerGroupKeys = {
  all: ['customer-groups'] as const,
  list: () => [...customerGroupKeys.all, 'list'] as const,
}

export function useCustomerGroupsList() {
  return useQuery({
    queryKey: customerGroupKeys.list(),
    queryFn: async (): Promise<CustomerGroup[]> => {
      const data = await api.get<CustomerGroupsListResponse | CustomerGroup[]>(
        '/ht/api/select-customer-groups'
      )
      if (Array.isArray(data)) return data
      return data?.customer_groups ?? data?.groups ?? []
    },
    staleTime: 1000 * 60 * 30,
  })
}

// ---------------------------------------------------------------------------
// SpringBig loyalty — balance / rewards / offers / redemption history
// GET /v2/.../customers/:id/springbig
//
// Always 200 for soft states (store off, customer not enrolled): the FE
// reads `loyalty_enabled` and `customer_enrolled` to decide what to render.
// HTTP errors only fire on real failures: 404 customer, 502 SpringBig
// outage. Partial failures (offers/redemptions parse error) degrade to
// empty arrays — treat as "nothing to show right now," not "outage."
// ---------------------------------------------------------------------------

export interface SpringbigReward {
  id: number
  item_value: number
  discount_type?: string
  discount_amount?: number | string
  [k: string]: unknown
}

export interface SpringbigOffer {
  id: number
  redemptions_available: number
  active: boolean
  discount_type?: string
  discount_amount?: number | string
  expiration?: string | null
  [k: string]: unknown
}

export interface SpringbigRedemption {
  id: number
  status: string
  points_redeemed?: number
  [k: string]: unknown
}

export interface CustomerSpringbigResponse {
  loyalty_enabled: boolean
  customer_enrolled: boolean
  balance: number
  rewards: SpringbigReward[]
  offers: SpringbigOffer[]
  redemptions: SpringbigRedemption[]
}

export const customerSpringbigKey = (customerId: number) =>
  [...customerKeys.detail(customerId), 'springbig'] as const

export function useCustomerSpringbig(customerId: number | null) {
  return useQuery({
    queryKey: customerSpringbigKey(customerId!),
    queryFn: async (): Promise<CustomerSpringbigResponse> => {
      return v2api.get<CustomerSpringbigResponse>(
        v2(`/customers/${customerId}/springbig`),
      )
    },
    enabled: !!customerId,
    // 4 sequential SpringBig API calls per request — don't hammer it.
    // The drawer-open is the natural refresh trigger.
    staleTime: 30 * 1000,
    networkMode: 'online',
  })
}

// Get customer purchase limits (cannabis compliance)
export function useCustomerLimits(customerId: number | null) {
  return useQuery({
    queryKey: [...customerKeys.detail(customerId!), 'limits'],
    queryFn: async () => {
      return v2api.get<{
        daily_limit_grams: number
        daily_purchased_grams: number
        remaining_grams: number
      }>(v2(`/customers/${customerId}/limits`))
    },
    enabled: !!customerId,
    staleTime: 1000 * 30, // 30 seconds - limits change with each sale
  })
}
